/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useState, useCallback } from 'react';

/**
 * Hook for managing multi-select state for any entity list
 * Supports individual selection, select all, and respects current filters
 */
export function useClipSelection<T extends { id: string }>(
  items: T[],
  filteredItems: T[]
) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  /**
   * Toggle selection for a single item
   * Can accept either an event (for click handlers) or any value (for checkbox onChange)
   */
  const toggleSelection = useCallback((id: string, eventOrValue?: any) => {
    // Stop propagation if it's an event object
    if (eventOrValue && typeof eventOrValue === 'object' && 'stopPropagation' in eventOrValue) {
      eventOrValue.stopPropagation();
    }

    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  /**
   * Toggle all filtered items
   * If all filtered items are selected, deselect them
   * Otherwise, select all filtered items
   */
  const toggleAll = useCallback(() => {
    const filteredIds = filteredItems.map(item => item.id);
    const allFilteredSelected = filteredIds.every(id => selectedIds.has(id));

    if (allFilteredSelected) {
      // Deselect all filtered items
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      // Select all filtered items (preserve existing selections)
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredIds.forEach(id => next.add(id));
        return next;
      });
    }
  }, [filteredItems, selectedIds]);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  /**
   * Select specific items by IDs
   */
  const selectItems = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  /**
   * Get the actual selected items from the full list
   */
  const selectedItems = items.filter(item => selectedIds.has(item.id));

  /**
   * Check if all filtered items are selected
   */
  const isAllSelected = filteredItems.length > 0 &&
    filteredItems.every(item => selectedIds.has(item.id));

  /**
   * Check if some (but not all) filtered items are selected
   */
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  return {
    selectedIds,
    selectedItems,
    selectedCount: selectedIds.size,
    toggleSelection,
    toggleAll,
    clearSelection,
    selectItems,
    isAllSelected,
    isSomeSelected
  };
}
